import { useEffect } from 'react'

import studioPreviewImg from '/bot-type-studio-preview.png'
import adkPreviewImg from '/bot-type-adk-preview.png'
import { trackEvent } from '~/providers'

export type BotType = 'studio' | 'adk'

type BotTypeSelectionOverlayProps = {
  onSelect: (type: BotType) => void
  onClose: () => void
}

const BotTypeSelectionOverlay = ({ onSelect, onClose }: BotTypeSelectionOverlayProps) => {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Track dialog presented
  useEffect(() => {
    trackEvent({ type: 'bot_type_dialog_presented' })
  }, [])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bot-type-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={handleBackdropClick}
    >
      <div className="flex max-h-full w-full flex-col items-center gap-8 overflow-visible py-8 md:gap-12 md:py-0">
        {/* Header */}
        <div className="flex flex-col items-center gap-3 text-center">
          <h2 id="bot-type-dialog-title" className="text-2xl font-semibold text-white md:text-4xl">
            How do you want to build?
          </h2>
          <p className="text-sm text-muted-foreground">Choose what's best for you. This can't be changed later.</p>
        </div>

        {/* Cards */}
        <div className="flex flex-col gap-6 overflow-visible px-4 md:flex-row md:gap-10 md:px-0">
          {/* Studio Card - always light */}
          <div className="group relative">
            <div className="group-hover:animate-pulse-glow absolute -inset-2 rounded-xl bg-gradient-to-r from-cyan-400 via-blue-500 to-blue-600 opacity-0 blur-lg transition-opacity duration-300 group-hover:opacity-75" />
            <button
              type="button"
              onClick={() => onSelect('studio')}
              className="relative flex h-[320px] w-full flex-col gap-4 rounded-lg border border-neutral-300 bg-neutral-100 p-4 text-left md:h-[420px] md:w-[480px] dark:border-neutral-600 dark:bg-neutral-900"
            >
              <div className="flex flex-col gap-1">
                <span className="text-xl font-medium text-neutral-900 dark:text-neutral-100">Botpress Studio</span>
                <span className="text-xs text-neutral-500">Build bots visually with a drag-and-drop canvas.</span>
              </div>
              <div className="flex-1 overflow-hidden rounded-lg">
                <img src={studioPreviewImg} alt="Studio preview" className="h-full w-full object-cover" />
              </div>
              <div className="flex flex-col text-xs">
                <span className="font-medium text-neutral-500">Best For</span>
                <span className="text-neutral-500">Most use cases.</span>
              </div>
            </button>
          </div>

          {/* ADK Card - always dark */}
          <div className="group relative">
            <div className="group-hover:animate-pulse-glow absolute -inset-2 rounded-xl bg-gradient-to-r from-purple-500 via-violet-500 to-fuchsia-500 opacity-0 blur-lg transition-opacity duration-300 group-hover:opacity-75" />
            <button
              type="button"
              onClick={() => onSelect('adk')}
              className="relative flex h-[320px] w-full flex-col gap-4 rounded-lg border border-neutral-700 bg-neutral-800 p-4 text-left md:h-[420px] md:w-[480px] dark:border-neutral-300 dark:bg-neutral-200"
            >
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-xl font-medium text-neutral-100 dark:text-neutral-900">
                    Agent Development Kit
                  </span>
                  <span className="rounded border border-blue-500 bg-blue-950 px-2 py-0.5 text-xs font-semibold text-blue-400">
                    Beta
                  </span>
                </div>
                <span className="text-xs text-neutral-400 dark:text-neutral-600">
                  Build agents in TypeScript with full control.
                </span>
              </div>
              <div className="flex-1 overflow-hidden rounded-lg">
                <img src={adkPreviewImg} alt="ADK preview" className="h-full w-full object-cover" />
              </div>
              <div className="flex flex-col text-xs">
                <span className="font-medium text-neutral-500">Best For</span>
                <span className="font-light text-neutral-400 dark:text-neutral-600">
                  Developers who want to go further.
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export const showBotTypeSelectionDialog = (): Promise<BotType | undefined> => {
  return new Promise((resolve) => {
    const container = document.createElement('div')
    document.body.appendChild(container)

    let reactRoot: { unmount: () => void; render: (children: React.ReactNode) => void } | null = null

    const cleanup = () => {
      reactRoot?.unmount()
      container.remove()
    }

    const handleSelect = (type: BotType) => {
      trackEvent({ type: 'bot_type_selected', botType: type })
      cleanup()
      resolve(type)
    }

    const handleClose = () => {
      trackEvent({ type: 'bot_type_dialog_dismissed' })
      cleanup()
      resolve(undefined)
    }

    import('react-dom/client').then(({ createRoot }) => {
      reactRoot = createRoot(container)
      reactRoot.render(<BotTypeSelectionOverlay onSelect={handleSelect} onClose={handleClose} />)
    })
  })
}
